import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { T } from "@/lib/theme";
import OrbitViewer from "@/components/OrbitViewer";
import { OrbitCloseTransit } from "@/pages/OrbitClosePreview";
import { BRANDS } from "@/lib/brands";
import EMark from "@/components/EMark";

/**
 * Orbit page — dedicated interactive stage for the brand orbit.
 * Pre-selects a brand if `?brand=<id>` is provided (e.g. from footer links).
 * Closing strip beneath the viewer reinforces the "one centre, many trajectories" idea.
 */
export default function Orbit() {
  const location = useLocation();
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const b = params.get("brand");
    if (b && BRANDS.find((x) => x.id === b)) setInitial(b);
  }, [location.search]);

  return (
    <div data-testid="orbit-page">
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
          backgroundSize: "52px 52px",
          maskImage: "radial-gradient(ellipse 70% 70% at 50% 40%, black, transparent 85%)",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
            <span className="inline-flex items-center gap-2"><EMark /> · ORBIT · MMXX</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>Interactive · 05 brands</span>
          </div>

          <div className="mt-12 max-w-4xl">
            <span className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
              ORBIT
            </span>
            <h1 className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] mt-6" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
              A structured orbit of brands around a <span className="italic" style={{ color: T.accent }}>central core</span>.
            </h1>
            <p className="mt-8 max-w-xl font-intertight text-[16px] leading-[1.85]" style={{ color: T.textSoft }}>
              Choose a brand to bring it into focus. Each one holds a distinct role within the wider Erenown
              orbit. Click the centred brand again to return to the default view.
            </p>
          </div>

          <div className="mt-16">
            <OrbitViewer initialBrand={initial} />
          </div>
        </div>
      </section>

      {/* Closing — Option B: The Transit Log */}
      <OrbitCloseTransit />
    </div>
  );
}
